<script setup>
import {ref} from "vue";
import Swal from "sweetalert2";
import axios from "axios";

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  currentValue: {
    type: String,
    required: true,
  },
  enableErrors: {
    type: Boolean,
    required: false,
    default: true,
  },
  refreshOnSuccess: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  confirmBeforeSave: {
    type: Boolean,
    required: false,
    default: false,
  },
  confirmTitle: {
    type: String,
    required: false,
    default: 'Are you sure?',
  },
  confirmText: {
    type: String,
    required: false,
    default: null,
  },
  type: {
    type: String,
    default: 'text',
  }
});

const emit = defineEmits(['success', 'error']);

const value = ref(props.currentValue);
const showInput = ref(false);
const savingInProgress = ref(false);

const save = async () => {
  if (props.confirmBeforeSave) {
    const confirmText = props.confirmText || `You are about to change the value from ${props.currentValue} to ${value.value}`;

    const result = await Swal.fire({
      title: props.confirmTitle,
      text: confirmText,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, update it!'
    });

    if (!result.isConfirmed) {
      return;
    }
  }

  savingInProgress.value = true;

  try {
    const {data} = await axios.post(props.url, {
      value: value.value
    });

    console.log(data);

    emit('success', value.value);
    if (props.refreshOnSuccess) {
      window.location.reload();
    }

    if (props.confirmBeforeSave) {
      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: data.message ?? 'The value has been updated successfully.',
        footer: data.subMessage ?? '',
      });
    }
  } catch (error) {
    // Reset value back to original on error
    value.value = props.currentValue;

    if (props.enableErrors) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response.data.message,
        footer: 'If you are unsure what is happening, please contact the dev team.'
      })
    }
    emit('error', value.value);
  }

  savingInProgress.value = false;
  showInput.value = false;
};

</script>

<template>
  <div class="d-flex justify-content-end tailwind">
    <div
      v-if="showInput && !disabled"
      class="d-flex align-items-center border"
    >
      <input
        v-model="value"
        :type="type"
        class="border-0"
      >
      <i
        v-if="savingInProgress"
        class="fas fa-spinner fa-spin ml-1"
      />
      <template v-else>
        <button
          class="bg-white border-0 save-btn"
          @click="save"
        >
          <i
            class="far fa-check icon-btn"
          />
        </button>
        <button
          class="bg-white border-0"
          @click="showInput = false"
        >
          <i
            role="button"
            class="far fa-times icon-btn px-1"
          />
        </button>
      </template>
    </div>
    <div
      v-else
      class="d-flex align-items-center"
    >
      <slot />
      <i
        v-if="!disabled"
        role="button"
        class="far fa-edit ml-2"
        @click="showInput = true"
      />
    </div>
  </div>
</template>

<style scoped>
i {
  font-size: 16px;
}

input {
  min-width: 200px;
  border-right: 1px solid #dee2e6 !important;
}

.save-btn {
  border-right: 1px solid #dee2e6 !important;
}
</style>
